import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  LocalShipping,
  Apartment,
  LocationOn,
  Phone,
  Image as ImageIcon,
} from "@mui/icons-material";
import api from "../../api/axios";
import socket from "../../socket";

const AgentOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const agent = JSON.parse(localStorage.getItem("user"));

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [nextStatus, setNextStatus] = useState(null);

  // ⛔ Protect agent route
  useEffect(() => {
    if (!agent?._id) {
      navigate("/agent/login", { replace: true });
      return;
    }

    socket.emit("agent_join", agent._id);
    return () => {
      socket.emit("agent_leave", agent._id);
    };
  }, [agent, navigate]);

  // 📦 Fetch order details
  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/agent/orders/${orderId}`);
      setOrder(data);

      /* Join order tracking room */
      socket.emit("join_order", orderId);
    } catch (error) {
      console.error("Failed to load order:", error);
      navigate("/agent/orders", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate]);

  useEffect(() => {
    if (agent?._id && orderId) {
      fetchOrder();
    }
  }, [agent?._id, orderId, fetchOrder]);

  // 📡 Listen for real-time updates
  useEffect(() => {
    socket.on("order_status_updated", (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
        }));
      }
    });

    socket.on("preparation_update", (data) => {
      if (data.orderId === orderId) {
        console.log("📸 Vendor sent preparation update:", data);
        setOrder((prev) => ({
          ...prev,
          preparationUpdates: data.preparationUpdates || prev.preparationUpdates,
          vendorPhase: data.vendorPhase || prev.vendorPhase,
        }));
      }
    });

    socket.on("vendor_preparing", (data) => {
      if (data.orderId === orderId) {
        console.log("⏳ Vendor started preparing:", data);
        fetchOrder(); // Refresh to get latest status
      }
    });

    return () => {
      socket.off("order_status_updated");
      socket.off("preparation_update");
      socket.off("vendor_preparing");
    };
  }, [orderId, fetchOrder]);

  // Get next allowed delivery action
  const getNextDeliveryAction = () => {
    const deliveryPhase = order?.deliveryPhase?.status;
    const actions = {
      NONE: "PICKED_UP",
      PICKED_UP: "IN_TRANSIT",
      IN_TRANSIT: "DELIVERED",
    };
    return actions[deliveryPhase] || null;
  };

  const getDeliveryActionLabel = (action) => {
    const labels = {
      PICKED_UP: "Mark as Picked Up",
      IN_TRANSIT: "Mark as In Transit",
      DELIVERED: "Mark as Delivered",
    };
    return labels[action] || "Update Status";
  };

  // 🔄 Update delivery status (new phase-based)
  const handleUpdateStatus = async () => {
    if (!nextStatus) return;

    try {
      setLoading(true);
      const endpointMap = {
                  PICKED_UP: "picked_up",
                  IN_TRANSIT: "in_transit",
                  DELIVERED: "delivered",
                };
      const endpoint =   `/agent/orders/${orderId}/${endpointMap[nextStatus]}`;
      await api.post(endpoint, {});

      // Refetch to get updated order
      await fetchOrder();
      setConfirmDialog(false);
      setNextStatus(null);

      // Show success message
      alert("Order status updated successfully!");
    } catch (error) {
      console.error("Failed to update status:", error);
      alert(error.response?.data?.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  // Format status labels
  const getStatusLabel = (status) => {
    return status ? status.replace(/_/g, " ") : "";
  };

  // Get status color
  const getStatusColor = (status, completed) => {
    if (completed) return "success";
    return status === order?.status ? "primary" : "default";
  };

  if (!order) {
    return (
      <Container sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          onClick={() => navigate("/agent/orders")}
          sx={{ mb: 2 }}
        >
          ← Back to Orders
        </Button>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
          Order #{orderId.substring(0, 8).toUpperCase()}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Track and update delivery status
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column: Order Details */}
        <Grid item xs={12} md={8}>
          {/* Order Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Order Summary
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Order Total
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      ₹{order.total}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Payment Method
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {order.paymentMethod?.toUpperCase()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Items */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Items ({order.items.length})
                </Typography>
                <Stack spacing={1}>
                  {order.items.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: 1,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2">{item.title}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Qty: {item.qty}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        ₹{item.price}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </CardContent>
          </Card>

          {/* 🟣 VENDOR PREPARATION STATUS - NEW */}
          {order.vendorPhase?.status && (
            <Card sx={{ mb: 3, border: "2px solid #9c27b0", backgroundColor: "#f3e5f5" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#7b1fa2" }}>
                  🟣 Vendor Preparation Status
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Current Status
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={order.vendorPhase.status}
                      color={
                        order.vendorPhase.status === "READY_FOR_PICKUP"
                          ? "success"
                          : order.vendorPhase.status === "PREPARING"
                          ? "warning"
                          : "default"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>

                {/* Estimated Ready Time */}
                {order.vendorPhase.estimatedReadyTime && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Estimated Ready Time
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                      {new Date(order.vendorPhase.estimatedReadyTime).toLocaleTimeString()}
                    </Typography>
                  </Box>
                )}

                {/* Preparation Updates - Images & Notes */}
                {order.preparationUpdates && order.preparationUpdates.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                      <ImageIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      Preparation Updates
                    </Typography>
                    <Stack spacing={2}>
                      {order.preparationUpdates.map((update, idx) => (
                        <Paper key={idx} sx={{ p: 2, backgroundColor: "#ffffff", border: "1px solid #e0e0e0" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                            <Typography variant="caption" fontWeight={600}>
                              {update.addedBy}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(update.createdAt).toLocaleString()}
                            </Typography>
                          </Box>
                          {update.imageUrl && (
                            <Box sx={{ mb: 1, borderRadius: 1, overflow: "hidden", backgroundColor: "#f5f5f5" }}>
                              <img
                                src={update.imageUrl}
                                alt="Preparation"
                                style={{ width: "100%", maxHeight: "200px", objectFit: "cover" }}
                              />
                            </Box>
                          )}
                          {update.notes && (
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                              {update.notes}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Delivery Address */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                <LocationOn sx={{ mr: 1, verticalAlign: "middle" }} />
                Delivery Address
              </Typography>

              <Paper sx={{ p: 2, backgroundColor: "#f9f9f9" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {order.address.addressLine1}
                </Typography>
                {order.address.addressLine2 && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.address.addressLine2}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {order.address.city}, {order.address.state}{" "}
                  {order.address.pincode}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {order.address.country}
                </Typography>
              </Paper>
            </CardContent>
          </Card>

          {/* Tracking Timeline */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                <LocalShipping sx={{ mr: 1, verticalAlign: "middle" }} />
                Delivery Timeline
              </Typography>

              <Stack spacing={1}>
                {order.tracking &&
                  order.tracking.map((track, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: 1.5,
                        backgroundColor: track.completed ? "#f0f7ff" : "#fafafa",
                        borderRadius: 1,
                        borderLeft: track.completed ? "4px solid #4caf50" : "4px solid #ccc",
                      }}
                    >
                      <CheckCircle
                        sx={{
                          fontSize: 20,
                          color: track.completed ? "#4caf50" : "#ccc",
                          mr: 2,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {getStatusLabel(track.key)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(track.time).toLocaleString()}
                        </Typography>
                      </Box>
                      {track.completed && (
                        <Chip label="Completed" color="success" size="small" />
                      )}
                    </Box>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Delivery Phase Update */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 20 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                🟡 Delivery Phase
              </Typography>

              {/* Current Delivery Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="textSecondary">
                  Current Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    icon={<CheckCircle />}
                    label={order.deliveryPhase?.status || "NONE"}
                    color={
                      order.deliveryPhase?.status === "DELIVERED"
                        ? "success"
                        : "primary"
                    }
                    sx={{ width: "100%" }}
                  />
                </Box>
              </Box>

              {/* Next Delivery Action */}
              {getNextDeliveryAction() && order.deliveryPhase?.status !== "DELIVERED" ? (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Next Step
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {getDeliveryActionLabel(getNextDeliveryAction())}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      setNextStatus(getNextDeliveryAction());
                      setConfirmDialog(true);
                    }}
                    disabled={loading || order.deliveryPhase?.status === "DELIVERED"}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      getDeliveryActionLabel(getNextDeliveryAction())
                    )}
                  </Button>
                </Box>
              ) : order.deliveryPhase?.status === "DELIVERED" ? (
                <Chip
                  label="✓ Order Delivered"
                  color="success"
                  sx={{ width: "100%" }}
                />
              ) : null}

              {/* Divider */}
              <Box sx={{ my: 3, borderTop: "1px solid #e0e0e0" }} />

              {/* Agent Phase Status */}
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Agent Phase Status
                </Typography>
                <Chip
                  label={order.agentPhase?.status || "NONE"}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>

              {/* Payment Status */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Payment Status
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    mt: 1,
                    color:
                      order.payment?.status === "PAID" ? "#4caf50" : "#ff9800",
                  }}
                >
                  {order.payment?.status || "PENDING"}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Update Delivery Status?</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Action
            </Typography>
            <Chip
              label={getDeliveryActionLabel(nextStatus)}
              color="primary"
              variant="outlined"
            />

            <Box sx={{ mt: 3, p: 2, backgroundColor: "#f5f5f5" }}>
              <Typography variant="caption" color="textSecondary">
                Order #{orderId.substring(0, 8).toUpperCase()}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                ₹{order.total}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AgentOrderDetails;
